import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const Row = ({ c, checked, onToggle }) => (
  <label className={`grid grid-cols-[24px_1fr_80px_80px_80px] items-center gap-2 border-b px-3 py-2 text-sm ${c.protected ? "text-muted-foreground" : "cursor-pointer hover:bg-muted/40"}`} data-testid={`restore-row-${c.name}`}>
    {c.protected ? <Lock className="h-3.5 w-3.5" /> : <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`restore-check-${c.name}`} />}
    <span>{c.label}{c.label !== c.name && <span className="ml-1 font-mono text-[10px] text-muted-foreground">{c.name}</span>}</span>
    <span className="text-right font-mono text-xs">{c.current}</span>
    <span className="text-right font-mono text-xs">{c.backup}</span>
    <span className={`text-right font-mono text-xs font-semibold ${c.diff < 0 ? "text-red-700" : c.diff > 0 ? "text-emerald-700" : "text-muted-foreground"}`}>{c.diff > 0 ? `+${c.diff}` : c.diff}</span>
  </label>
);

export const RestoreDialog = ({ backup, onClose, onDone }) => {
  const [p, setP] = useState(null);
  const [sel, setSel] = useState([]);
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setP(null); setSel([]); setWord("");
    if (backup) api.get(`/privacy/backups/${backup.id}/preview`).then((r) => setP(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [backup]); // eslint-disable-line react-hooks/exhaustive-deps
  const restorable = p ? p.collections.filter((c) => !c.protected) : [];
  const toggle = (n) => setSel((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/privacy/backups/${backup.id}/restore`, { collections: sel, confirm: word });
      toast[Object.keys(r.data.failed || {}).length ? "warning" : "success"](Object.keys(r.data.failed || {}).length ? `Ripristinati ${Object.keys(r.data.restored).length} archivi; non riusciti (lasciati com'erano): ${Object.keys(r.data.failed).join(", ")}` : `Ripristinati ${Object.keys(r.data.restored).length} archivi. Backup di sicurezza dello stato precedente salvato.`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!backup} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="restore-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Ripristina backup del {backup && fmtDateTime(backup.at)}</DialogTitle></DialogHeader>
        {!p ? <p className="py-8 text-center text-sm text-muted-foreground">Lettura del backup…</p> : <>
          <p className="text-sm text-muted-foreground">Scegli gli archivi da riportare allo stato del backup: i dati attuali di quegli archivi verranno sostituiti. Prima del ripristino viene salvato in automatico un backup di sicurezza. Utenti, password, accessi e registro modifiche non vengono mai toccati.</p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setSel(restorable.map((c) => c.name))} data-testid="restore-select-all">Seleziona tutto</Button>
            <Button size="sm" variant="ghost" onClick={() => setSel([])} data-testid="restore-select-none">Nessuno</Button>
            <span className="ml-auto text-xs text-muted-foreground" data-testid="restore-selected-count">{sel.length} di {restorable.length} selezionati</span>
          </div>
          <div className="border">
            <div className="grid grid-cols-[24px_1fr_80px_80px_80px] gap-2 bg-muted/50 px-3 py-2 micro-label"><span /><span>Archivio</span><span className="text-right">Oggi</span><span className="text-right">Nel backup</span><span className="text-right">Differenza</span></div>
            {p.collections.map((c) => <Row key={c.name} c={c} checked={sel.includes(c.name)} onToggle={() => toggle(c.name)} />)}
          </div>
          <div className="border-l-4 border-red-600 bg-red-50 px-4 py-3 text-sm">
            <div className="font-semibold text-red-900">Per confermare scrivi {p.confirm_word}</div>
            <Input className="mt-2 max-w-xs bg-white" value={word} onChange={(e) => setWord(e.target.value)} placeholder={p.confirm_word} data-testid="restore-confirm-input" />
          </div>
        </>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button variant="destructive" onClick={go} disabled={busy || !sel.length || word.trim().toUpperCase() !== p?.confirm_word} data-testid="restore-submit">{busy ? "Ripristino in corso…" : `Ripristina ${sel.length} archivi`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
